import React, { useState } from "react"
import { CircularProgress, IconButton, Tooltip } from "@mui/material"
import { Download } from "@mui/icons-material"
import { useSnackbar } from "@/shared/context/SnackbarContext"

interface Props {
  imageUrls: string[]
  selectedImageUrls?: string[]
  productName: string
}

const DownloadImagesButton: React.FC<Props> = ({ imageUrls, selectedImageUrls, productName }) => {
  const [downloading, setDownloading] = useState(false)
  const { showSnackbar } = useSnackbar()

  const sanitizedName = (productName || "image").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "")
  const hasImages = imageUrls.length > 0
  const selectedCount = selectedImageUrls?.length ?? 0
  const urlsToDownload = selectedCount > 0 ? (selectedImageUrls ?? []) : imageUrls

  const tooltipTitle = !hasImages
    ? "No generated images to download"
    : downloading
      ? "Downloading…"
      : selectedCount === 0
        ? "Download all images"
        : `Download ${selectedCount} selected image${selectedCount === 1 ? "" : "s"}`

  const handleDownload = async () => {
    if (!hasImages) return
    setDownloading(true)
    try {
      for (let i = 0; i < urlsToDownload.length; i++) {
        const response = await fetch(urlsToDownload[i])
        if (!response.ok) throw new Error(`Failed to fetch image ${i + 1}`)
        const blob = await response.blob()
        const ext = blob.type.split("/")[1] || "jpg"
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = blobUrl
        link.download = `${sanitizedName}_${i + 1}.${ext}`
        link.click()
        URL.revokeObjectURL(blobUrl)
        if (i < urlsToDownload.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300))
        }
      }
    } catch (error) {
      console.error("Error downloading images:", error)
      showSnackbar("Failed to download images. Please try again.", "error")
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          size="small"
          color="primary"
          onClick={handleDownload}
          disabled={!hasImages || downloading}
          aria-label="download images">
          {downloading ? <CircularProgress size={18} color="inherit" /> : <Download />}
        </IconButton>
      </span>
    </Tooltip>
  )
}

export default DownloadImagesButton
